import React, { useCallback, useEffect, useState } from 'react';
import { api, optimisticUpdate } from '@gik/core/api';
import { UI } from '@gik/ui';
import { gikClassPrefix } from '@gik/core/constants';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import PopoverListItem from '@gik/ui/PopoverListItem';
import InkindListItem, { IInkindListItemProps } from '@gik/ui/gik/InkindListItem';
import { useUserStore } from '@gik/core/store/UserStore';
import { getCDNAssetsUrl } from '@gik/core/utils/LinkUtils';
import { useUserOwnedOrOrganizingPages } from '@gik/api/inkinds/userPages';
import LoadingSpinner from '@gik/ui/LoadingSpinner';
import { useWishlistProducts } from '@gik/api/inkinds/wishlistProducts';

const blockName = `${gikClassPrefix}-add-to-wishlist-popover-content`;

interface AddToWishlistPopoverContentProps {
  productId: number;
  inkindRouteId?: string; // optional, when viewing an inkind page or wishlist
  closePopover?(): void;
}

export default function AddToWishlistPopoverContent({
  productId,
  inkindRouteId,
  closePopover,
}: AddToWishlistPopoverContentProps): React.ReactElement {
  const userStore = useUserStore();
  const userId = userStore.id;
  const [doAutoAction, setDoAutoAction] = useState(true);

  const {
    data: inkindPages,
    mutate: refreshInkindPages,
    isValidating: isLoadingInkindPages,
  } = useUserOwnedOrOrganizingPages(userId);

  const { data: currentPageWishListProducts, mutate: refreshCurrentPageWishListProducts } = useWishlistProducts(
    inkindRouteId
  );

  const inkindListItems: IInkindListItemProps[] = (inkindPages || []).map(inkindPage => ({
    name: inkindPage.title,
    thumbnail: inkindPage.thumb_small,
    isOrganizer: true,
    routeId: inkindPage.routeId,
  }));

  const updateWishlistItem = useCallback(
    async (inkindListItem: IInkindListItemProps) => {
      const matchingPage = inkindPages.find(page => page.routeId === inkindListItem.routeId);
      const isAdded = matchingPage.wishlistProductIds?.some(id => id === productId);
      const addToWishlist = !isAdded;

      const executeUpdate = async () => {
        const wishlistProductUrl = `inkinds/${matchingPage.routeId}/wishlistProducts/${productId}`;
        if (addToWishlist) {
          try {
            await api.put(wishlistProductUrl);
            UI.notifySuccess('Item successfully added to wishlist');
          } catch (err) {
            UI.notifyError('Failed to add item to wishlist');
          }
        } else {
          try {
            await api.delete(wishlistProductUrl);
            UI.notifySuccess('Item successfully removed from wishlist');
          } catch (err) {
            UI.notifyError('Failed to remove item from wishlist');
          }
        }
      };

      const isViewingInkindPage = inkindRouteId && matchingPage.routeId === inkindRouteId;

      if (currentPageWishListProducts && isViewingInkindPage) {
        // optimistic update for currently viewed inkind/wishlist page
        // - only removals implemented since products cannot currently
        // be added to the wishlist grid from the inkind/wishlist page
        if (!addToWishlist) {
          refreshCurrentPageWishListProducts(
            // access the items ref, since wishlist toolbar does not have access to latest items
            currentPageWishListProducts.filter(item => item.id !== productId),
            false
          );
        }
      }
      const updatedPages = inkindPages.slice();
      updatedPages[updatedPages.indexOf(matchingPage)] = {
        ...matchingPage,
        wishlistProductIds: [
          ...matchingPage.wishlistProductIds?.filter(id => id !== productId),
          ...(isAdded ? [] : [productId]),
        ],
      };

      await optimisticUpdate(updatedPages, refreshInkindPages, executeUpdate);

      if (currentPageWishListProducts && isViewingInkindPage) {
        // finish optimistic update for currently viewed page
        refreshCurrentPageWishListProducts();
      }
    },
    [
      inkindPages,
      productId,
      refreshInkindPages,
      currentPageWishListProducts,
      refreshCurrentPageWishListProducts,
      inkindRouteId,
    ]
  );

  const isProductInWishlist = (page: InkindPageAPIModel) => page.wishlistProductIds?.some(id => id === productId);

  function renderWishlistItem(inkindListItem: IInkindListItemProps): React.ReactElement {
    const matchingPage = inkindPages.find(page => page.routeId === inkindListItem.routeId);
    const isAdded = isProductInWishlist(matchingPage);
    return (
      <PopoverListItem
        key={inkindListItem.routeId}
        prepend={<InkindListItem {...inkindListItem} />}
        icon={isAdded ? 'close' : 'add'}
        danger={isAdded}
        iconSize="lg"
        hover
        onClick={() => updateWishlistItem(inkindListItem)}
      />
    );
  }

  const hasSinglePage = inkindListItems.length === 1;

  useEffect(() => {
    if (doAutoAction && hasSinglePage) {
      setDoAutoAction(false);
      updateWishlistItem(inkindListItems[0]);
      closePopover();
    }
  }, [hasSinglePage, doAutoAction, setDoAutoAction, updateWishlistItem, closePopover, inkindListItems]);

  return (
    <div className={blockName}>
      <h6>ADD TO WHICH WISHLIST?</h6>
      {!inkindListItems.length && isLoadingInkindPages && <LoadingSpinner center />}
      {!inkindListItems.length && !isLoadingInkindPages && <p>You have no Inkind pages yet</p>}
      {inkindListItems.map(inkindListItem => renderWishlistItem(inkindListItem))}
    </div>
  );
}
